import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '@salesrun/ngx-logging';
import { UnauthenticatedError, UnauthorizedError } from '@salesrun/ngx-logging/errors';
import { SrpValidators } from '@salesrun/ngx-validation';
import moment, { Moment } from 'moment';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs';
import SwiperCore, { Autoplay, Pagination, SwiperOptions } from 'swiper';
import { AuthenticationMethods } from '../../../../../../src/shared/authentication/AuthenticationMethods';
import { LoginService } from '../login.service';


SwiperCore.use([Pagination, Autoplay]);


interface ICarouselItem {
    image: string;
    title: string;
    caption: string;
}

@Component({
    selector: 'sr-login-login-page',
    templateUrl: './login-page.view.html',
    styleUrls: ['./login-page.style.scss'],
    standalone: false,
})
export class LoginPageComponent implements OnInit {
    loginForm: FormGroupTyped<{ userName: string, password: string }>;
    pageTitle = 'Login';
    error = false;
    loading = false;
    errorMsg: string;
    carouselItems: ICarouselItem[];
    config: SwiperOptions = {
        threshold: 50,
        spaceBetween: 5,
        slidesPerView: 1,
        centeredSlides: true,
        pagination: true,
        autoplay: {
            delay: 3000,
        },
    };
    showPassword = false;
    utcNow: Moment = moment.utc();
    authenticationMethod?: AuthenticationMethods;

    get isPasswordLogin() {
        return this.authenticationMethod == AuthenticationMethods.Password;
    }

    get userName() {
        return this.loginForm.controls.userName.value.trim();
    }

    get password() {
        return this.loginForm.controls.password.value;
    }

    @ViewChild('password', { read: ElementRef, static: true }) passwordInput: ElementRef<HTMLInputElement>;

    constructor(
        private _login: LoginService,
        private _formBuilder: UntypedFormBuilder,
        private _route: ActivatedRoute,
        private _logger: LoggerService,
    ) {
        this.loginForm = this._formBuilder.group({
            userName: [null, SrpValidators.required],
            password: [null, SrpValidators.required],
        }) as FormGroupTyped<{ userName: string, password: string }>;

    }

    ngOnInit(): void {
        this.carouselItems = [{
            image: 'assets/images/productivity.jpg',
            caption: 'Get the most out of your day by streamlining the process with SalesRun',
            title: 'Productivity',
        }, {
            image: 'assets/images/success.jpg',
            caption: 'Build success with the most advanced Sales tools available from SalesRun',
            title: 'Success',
        }, {
            image: 'assets/images/team.jpg',
            caption: 'Integrate your entire sales team into one seamless platform with SalesRun',
            title: 'Team',
        }];

        this.loginForm.controls.userName.valueChanges.subscribe(() => {
            this.authenticationMethod = null;
        });
    }

    continue() {
        if (!this.loginForm.controls.userName.valid) {
            return;
        }
        this.error = false;
        this.loading = true;
        this._login.getAuthenticationMethod(this.userName)
            .pipe(catchError((error: Error) => {
                this.errorMsg = 'An error occured. Please, try again later.';
                this._logger.error(error);
                void this._logger.recordError(error);
                this.error = true;
                this.loading = false;
                return EMPTY;
            }))
            .subscribe(result => {
                this.authenticationMethod = result.method;
                if (this.authenticationMethod == AuthenticationMethods.Password) {
                    this.passwordInput.nativeElement.focus();
                }
                if (!result.signInUrl) {
                    this.loading = false;
                    return;
                }
                let returnUrl = this._route.snapshot.queryParamMap.get('ReturnUrl');
                returnUrl = returnUrl ? `?ReturnUrl=${returnUrl}` : '';
                location.href = `${result.signInUrl}${returnUrl}`;
            });
    }

    login(): void {
        if (!this.loginForm.valid) {
            return;
        }
        this.error = false;
        this.loading = true;
        this._login.login(this.userName, this.password)
            .pipe(catchError((error: Error) => {
                this.errorMsg = 'An error occured. Please, try again later.';
                if (error instanceof UnauthorizedError) {
                    this.errorMsg = error.message;
                }
                else if (error instanceof UnauthenticatedError) {
                    this.errorMsg = error.message;
                }
                else {
                    this._logger.error(error);
                    void this._logger.recordError(error);
                }
                this.error = true;
                this.loading = false;
                return EMPTY;
            }))

            .subscribe(result => {
                location.href = this._route.snapshot.queryParamMap.get('ReturnUrl') ?? result.redirectUrl;
            });
    }

    switchShowPassword(): void {
        this.showPassword = !this.showPassword;
    }
}
